<script>
import {debounceAsyncValidator} from "@/common/utils/index.js"
import useVuelidate from "@vuelidate/core"
import {helpers, required} from "@vuelidate/validators"
import axios from "axios"

const {withAsync, withMessage} = helpers

// move to provider eventually
async function userExists(loginName) {
  if (loginName === "") return true
  const response = await fetch(`/support/user/${loginName}/exists`)
  return Boolean(await response.json())
}

export default {
  props: {
    userType: {
      type: String,
      required: false,
      default: "",
    }
  },

  setup() {
    return {
      v$: useVuelidate(),
    }
  },

  data() {
    return {
      type: this.userType,
      firstName: "",
      lastName: "",
      userName: "",
      email: "",
      password: "",
      gradeLevel: "",
      howDidYouFindUs: "",

      showPassword: false,
      schoolZipCode: "",
      selectedSchool: "",

      schoolsLoaded: false,
      loadingSchools: false,
      availableSchools: [],

      errorMessage: "",
      submitting: false,
      confirmationEmail: "",

      // constant data

      gradeLevels: [
        "Kindergarten",
        "1st Grade",
        "2nd grade",
        "3rd grade",
        "4th grade",
        "5th grade",
        "6th grade or higher",
        "Multiple grade levels",
      ],

      referralTypes: [
        "Pinterest",
        "A Friend Referred Me",
        "Search Engine",
        "Facebook",
        "Twitter",
        "Instagram",
        "Other",
        "Unknown",
      ],

      userTypeOptions: [
        {item: 'teacher', name: 'School Teacher'},
        {item: 'homeschooler', name: 'Homeschooler'}
      ]
    }
  },

  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },

    onSubmit() {
      const payload = {
        type: this.type,
        firstName: this.firstName,
        lastName: this.lastName,
        userName: this.userName,
        email: this.email,
        password: this.password,
        gradeLevel: this.gradeLevel,
        howDidYouFindUs: this.howDidYouFindUs,
        selectedSchool: this.selectedSchool,
      }

      this.submitting = true
      axios.post("/teacher/signup", payload)
        .then(response => response.data)
        .then(data => {
          if (data["success"]) {
            if (data["step"] === "confirmation_sent") {
              this.confirmationEmail = data["email"]
            } else if (data["redirect"]) {
              window.location = data["redirect"]
            }
          } else {
            this.errorMessage = data["message"]
          }
        }).catch(error => {
        try {
          if (error.response.data.errors) {
            let msg = ""
            for (let k in error.response.data.errors) {
              msg += error.response.data.errors[k][0]
            }
            this.errorMessage = msg
          }
        } catch {
          this.errorMessage = error.toString()
        }
      }).finally(() => {
        this.submitting = false
      })
    },

    onZipChange() {
      if (this.schoolZipCode.length === 5) {
        this.lookupByZip(this.schoolZipCode)
      }
    },

    async lookupByZip(zip) {
      this.loadingSchools = true
      const response = await fetch(`/support/schoolsByZip/${zip}`)
      this.availableSchools = await response.json()
      this.loadingSchools = false
      this.schoolsLoaded = true
    },
  },

  validations() {
    return {
      userName: {
        required,
        isUnique: withMessage(
          "This username has already been taken.",
          withAsync(debounceAsyncValidator(function (value, debounce) {
            if (!value) return true
            // async validator.  don't try to refactor the promise chain, it depends on being two steps.
            return debounce()
              .then(() => userExists(value))
              .then(exists => !exists)
              .catch(() => false) // could be caused by either rest api failure or by debounce
          }, 500)),
        ),
      },
    }
  },
}
</script>

<template>
  <confirmation-sent v-if="confirmationEmail" :email="confirmationEmail" />
  <form v-else method="post" class="p-3" name="frmSignUp" @submit.prevent="onSubmit">
    <div v-if="errorMessage" class="alert alert-danger">
      <b>Error: </b>{{ errorMessage }}
    </div>

    <div class="row gx-3">
      <div class="col-sm-6">
        <label class="form-label" for="firstname">First name</label>
        <input
          v-model.trim="firstName"
          type="text"
          required
          maxlength="45"
          minlength="2"
          class="form-control"
          name="firstName"
        >
      </div>

      <div class="col-sm-6">
        <label class="form-label" for="lastname">Last name</label>
        <input
          v-model.trim="lastName"
          required
          maxlength="45"
          type="text"
          class="form-control"
          name="lastName"
        >
      </div>
    </div>

    <div class="mb-2">
      <label class="form-label" for="email">Email address</label>
      <input
        v-model="email"
        type="email"
        class="form-control"
        name="email"
        required
      >
    </div>

    <div class="mb-2">
      <label class="form-label" for="login">Username</label>
      <input
        v-model="v$.userName.$model"
        type="text"
        autocapitalize="none"
        autocomplete="off"
        class="form-control"
        maxlength="20"
        minlength="4"
        required
      />
      <div v-if="!v$.userName.$pending">
        <div v-for="error in v$.userName.$errors" :key="error.$uid" class="error">{{ error.$message }}</div>
      </div>
    </div>

    <div class="mb-2">
      <label class="form-label" for="txt_password">Password</label>
      <div class="input-group">
        <input
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          class="form-control"
          minlength="6"
          maxlength="32"
          required
        />
        <span
          class="eye-icon input-group-text"
          :title="showPassword ? 'Hide password' : 'Show password'"
          @click="togglePasswordVisibility"
        >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'" class="text-body ml-1"></i>
            </span>
      </div>
    </div>

    <div class="mb-2 d-flex align-items-baseline">
      <label class="form-label mr-3" for="type">I am a:</label>

      <div style="flex-grow: 1;">
        <b-form-radio-group

          v-model="type"
          name="type"
          :options="userTypeOptions"
          buttons
          button-variant="outline-primary"
          value-field="item"
          text-field="name"
          required
        />
      </div>
    </div>

        <div class="mb-2">
          <label class="form-label" for="inf_custom_GradeLevelofStudents">
            Grade Level of Students <em style="font-size: 0.75em">(won't limit your access to content)</em>
          </label>
          <select id="gradeLevel" v-model="gradeLevel" class="form-select" required>
            <option value="">--Select grade level--</option>
            <option v-for="(grade, index) in gradeLevels" :key="index" :value="grade">{{ grade }}</option>
          </select>
        </div>

    <div v-if="type === 'teacher'">
      <div class="row gx-3">
        <div class="col-sm-6">
          <div class="mb-2">
            <label class="form-label" for="txt_password">School ZIP (5 digits)</label>
            <input v-model="schoolZipCode" type="number" class="form-control" maxlength="5" @input="onZipChange">
            <p v-show="loadingSchools"><strong>Loading Schools...</strong></p>
          </div>
        </div>

        <div class="col-sm-6">
          <div class="mb-2">
            <label class="form-label" for="school">Choose Your School</label>
            <select id="school" v-model="selectedSchool" name="school" class="form-select" required>
              <option value="">Choose Your School</option>
              <option v-for="school in availableSchools" :key="school.id" :value="school.id">
                {{ school.name }}
              </option>
              <option value="1">My School Is Not Listed</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <label class="form-label" for="referral">
        How Did You Find Out About Us?
      </label>
      <select
        id="referral"
        v-model="howDidYouFindUs"
        class="form-select"
        name="referral"
        required
      >
        <option value="">--Select a choice--</option>
        <option v-for="(how, index) in referralTypes" :key="index" :value="how">{{ how }}</option>
      </select>
    </div>

    <button id="btnSubmit" type="submit" class="btn btn-primary btn-lg d-block w-100">
      Start Your Free Trial
    </button>
  </form>
</template>

<style lang="scss" scoped>
@import "../../sass/colorscheme";

form {
  .error {
    color: $cs-l2l-red;
  }
}

.eye-icon {
  max-height: 38px;
  border-left: 0;
}
</style>
